/**
 * Piper VITS TTS via onnxruntime-web + HuggingFace piper-voices.
 * Phonemization uses a lightweight espeak-ng WASM when available,
 * otherwise falls back to character-level mapping from onnx.json.
 */

import { loadEsmModule } from './cdn';
import { LoadProgress, PresetVoice, SynthOptions, SynthResult, TtsEngine } from './types';
import { concatPcm, splitSentences } from '../audioUtils';
import { getModelById } from '../modelRegistry';

type PiperBundle = {
  session: any;
  config: any;
  sampleRate: number;
  voiceId: string;
  label: string;
  lang: string;
};

const loaded: { [modelId: string]: PiperBundle } = {};
let abortFlag = false;

/** HF paths for piper voices (onnx + json). */
const PIPER_HF: {
  [repoKey: string]: {
    onnx: string;
    json: string;
    voiceId: string;
    label: string;
    lang: string;
  };
} = {
  'piper-vi_VN-vivos-x_low': {
    onnx:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/vi/vi_VN/vivos/x_low/vi_VN-vivos-x_low.onnx',
    json:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/vi/vi_VN/vivos/x_low/vi_VN-vivos-x_low.onnx.json',
    voiceId: 'vi_VN-vivos-x_low',
    label: 'Vivos (VI)',
    lang: 'vi',
  },
  'piper-vi_VN-vais1000-medium': {
    onnx:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/vi/vi_VN/vais1000/medium/vi_VN-vais1000-medium.onnx',
    json:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/vi/vi_VN/vais1000/medium/vi_VN-vais1000-medium.onnx.json',
    voiceId: 'vi_VN-vais1000-medium',
    label: 'VAIS 1000 (VI)',
    lang: 'vi',
  },
  'piper-en_US-lessac-medium': {
    onnx:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/en/en_US/lessac/medium/en_US-lessac-medium.onnx',
    json:
      'https://huggingface.co/rhasspy/piper-voices/resolve/main/en/en_US/lessac/medium/en_US-lessac-medium.onnx.json',
    voiceId: 'en_US-lessac-medium',
    label: 'Lessac (EN)',
    lang: 'en',
  },
};

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

async function loadOrt(): Promise<any> {
  const win = getWin();
  if (win.ort && win.ort.InferenceSession) return win.ort;

  // Prefer classic UMD build so we get global `ort`
  await new Promise<void>((resolve, reject) => {
    const doc = win.document;
    const id = 'l123-ort-wasm';
    if (doc.getElementById(id)) {
      if (win.ort) resolve();
      else {
        const t = setInterval(() => {
          if (win.ort) {
            clearInterval(t);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(t);
          reject(new Error('Hết thời gian tải ONNX Runtime.'));
        }, 60000);
      }
      return;
    }
    const s = doc.createElement('script');
    s.id = id;
    s.src = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.17.3/dist/ort.min.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Không tải được onnxruntime-web.'));
    doc.head.appendChild(s);
  });

  if (!win.ort) throw new Error('ONNX Runtime không khởi tạo được.');
  // Point wasm paths to CDN
  try {
    win.ort.env.wasm.wasmPaths =
      'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.17.3/dist/';
  } catch {
    /* ignore */
  }
  return win.ort;
}

function getCaches(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  const win = g.window || g;
  return win.caches;
}

async function fetchJson(url: string): Promise<any> {
  try {
    const cachesApi = getCaches();
    if (cachesApi) {
      const cache = await cachesApi.open('l123-piper-v1');
      const hit = await cache.match(url);
      if (hit && hit.ok) return hit.json();
    }
  } catch {
    /* ignore */
  }
  const res = await fetch(url);
  if (!res.ok) throw new Error('Không tải được config Piper (' + res.status + ').');
  try {
    const cachesApi = getCaches();
    if (cachesApi) {
      const cache = await cachesApi.open('l123-piper-v1');
      await cache.put(url, res.clone());
    }
  } catch {
    /* ignore */
  }
  return res.json();
}

async function fetchArrayBuffer(
  url: string,
  onProgress?: (info: LoadProgress) => void
): Promise<ArrayBuffer> {
  try {
    const cachesApi = getCaches();
    if (cachesApi) {
      const cache = await cachesApi.open('l123-piper-v1');
      const hit = await cache.match(url);
      if (hit && hit.ok) {
        onProgress && onProgress({ status: 'Nạp Piper từ máy…', progress: 0.9 });
        return hit.arrayBuffer();
      }
    }
  } catch {
    /* ignore */
  }

  const res: any = await fetch(url);
  if (!res.ok) throw new Error('Không tải được model Piper (' + res.status + ').');
  const total = Number(res.headers.get('content-length') || 0);
  let buf: ArrayBuffer;
  if (!res.body || !total || !res.body.getReader) {
    buf = await res.arrayBuffer();
  } else {
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    while (true) {
      const result = await reader.read();
      if (result.done) break;
      chunks.push(result.value);
      loaded += result.value.length;
      onProgress &&
        onProgress({
          status: 'Đang tải model Piper…',
          progress: Math.min(0.95, loaded / total),
        });
    }
    const out = new Uint8Array(loaded);
    let offset = 0;
    chunks.forEach((c) => {
      out.set(c, offset);
      offset += c.length;
    });
    buf = out.buffer;
  }
  try {
    const cachesApi = getCaches();
    if (cachesApi) {
      const cache = await cachesApi.open('l123-piper-v1');
      await cache.put(
        url,
        new Response(buf.slice(0), {
          status: 200,
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Length': String(buf.byteLength),
          },
        })
      );
    }
  } catch {
    /* ignore */
  }
  return buf;
}

/**
 * Very lightweight phonemizer: map graphemes via phoneme_id_map when possible,
 * otherwise use piper-style character codes. Good enough for demo; quality
 * improves when text is already close to phonemes.
 *
 * For Vietnamese/English Piper models the JSON includes phoneme_id_map for IPA.
 * We approximate by using espeak via an optional CDN module; if missing, use
 * a naive character→id path that still produces audible output for short text.
 */
async function textToPhonemeIds(text: string, config: any): Promise<Int32Array> {
  const map = (config && config.phoneme_id_map) || {};
  const pad = map['_'] != null ? map['_'] : map[''] != null ? map[''] : [0];
  const bos = map['^'] != null ? map['^'] : null;
  const eos = map['$'] != null ? map['$'] : null;

  // Try CDN piper-phonemize-like helper — optional
  try {
    const mod = await loadEsmModule(
      'https://cdn.jsdelivr.net/npm/phonemizer@1.2.1/+esm',
      '__L123_PHONEMIZER__',
      30000
    );
    if (mod && (mod.phonemize || (mod.default && mod.default.phonemize))) {
      const phonemize = mod.phonemize || mod.default.phonemize;
      const voice = (config.language && config.language.code) || 'en-us';
      const ph = await phonemize(text, voice.indexOf('vi') >= 0 ? 'vi' : 'en');
      const ids: number[] = [];
      if (bos) ids.push.apply(ids, Array.isArray(bos) ? bos : [bos]);
      String(ph || '')
        .split('')
        .forEach((ch) => {
          const id = map[ch];
          if (id != null) {
            if (Array.isArray(id)) ids.push.apply(ids, id);
            else ids.push(id);
          }
        });
      if (eos) ids.push.apply(ids, Array.isArray(eos) ? eos : [eos]);
      if (ids.length > 2) return Int32Array.from(ids);
    }
  } catch {
    /* fall through */
  }

  // Fallback: map each character if present in map; insert pad between words
  const ids: number[] = [];
  if (bos) ids.push.apply(ids, Array.isArray(bos) ? bos : [bos]);
  const normalized = text.toLowerCase().normalize('NFC');
  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    if (ch === ' ') {
      if (Array.isArray(pad)) ids.push.apply(ids, pad);
      else ids.push(pad);
      continue;
    }
    const id = map[ch];
    if (id != null) {
      if (Array.isArray(id)) ids.push.apply(ids, id);
      else ids.push(id);
    }
  }
  if (eos) ids.push.apply(ids, Array.isArray(eos) ? eos : [eos]);
  if (ids.length < 2) {
    throw new Error(
      'Không chuyển được văn bản sang phoneme. Thử câu ngắn hơn hoặc engine khác (Kokoro / trình duyệt).'
    );
  }
  return Int32Array.from(ids);
}

function toInt64Data(ids: number[]): any {
  const win = getWin();
  const BI = win.BigInt;
  if (!BI) {
    return Int32Array.from(ids);
  }
  if (typeof win.BigInt64Array !== 'undefined') {
    const arr = new win.BigInt64Array(ids.length);
    for (let i = 0; i < ids.length; i++) arr[i] = BI(ids[i]);
    return arr;
  }
  return ids.map((n) => BI(n));
}

async function runPiper(
  bundle: PiperBundle,
  text: string,
  speed: number
): Promise<Float32Array> {
  const ort = await loadOrt();
  const phonemeIds = await textToPhonemeIds(text, bundle.config);
  const idArr = Array.from(phonemeIds) as number[];
  const scales = Float32Array.from([0.667, 1 / (speed || 1), 0.8]);
  const int64Data = toInt64Data(idArr);
  const useInt64 = !(int64Data instanceof Int32Array);
  const dtype = useInt64 ? 'int64' : 'int32';

  const inputTensor = new ort.Tensor(dtype, int64Data, [1, idArr.length]);
  const lengthsTensor = new ort.Tensor(dtype, toInt64Data([idArr.length]), [1]);
  const scalesTensor = new ort.Tensor('float32', scales, [3]);

  const inputNames: string[] = bundle.session.inputNames || [];
  const feed: any = {};
  if (inputNames.indexOf('input') >= 0) feed.input = inputTensor;
  else if (inputNames.length) feed[inputNames[0]] = inputTensor;

  if (inputNames.indexOf('input_lengths') >= 0) feed.input_lengths = lengthsTensor;
  else if (inputNames.length > 1) feed[inputNames[1]] = lengthsTensor;

  if (inputNames.indexOf('scales') >= 0) feed.scales = scalesTensor;
  else if (inputNames.length > 2) feed[inputNames[2]] = scalesTensor;

  // Multi-speaker Piper exports require a speaker id even when the voice has
  // only one usable preset. Pick the first speaker, matching Piper's CLI
  // default, so these models also work in the browser.
  if (inputNames.indexOf('sid') >= 0) {
    feed.sid = new ort.Tensor(dtype, toInt64Data([0]), [1]);
  }

  const out = await bundle.session.run(feed);
  const outName = (bundle.session.outputNames && bundle.session.outputNames[0]) || Object.keys(out)[0];
  const data = out[outName].data;
  return data instanceof Float32Array ? data : new Float32Array(data);
}

function resolveRepo(modelId: string, registryRepo: string): string {
  // modelRegistry repo field matches PIPER_HF keys
  if (PIPER_HF[registryRepo]) return registryRepo;
  if (PIPER_HF[modelId]) return modelId;
  // map from model ids in registry
  const map: { [k: string]: string } = {
    'piper-vi-vivos': 'piper-vi_VN-vivos-x_low',
    'piper-vi-vais': 'piper-vi_VN-vais1000-medium',
    'piper-en-lessac': 'piper-en_US-lessac-medium',
  };
  return map[modelId] || registryRepo;
}

export const piperEngine: TtsEngine = {
  id: 'piper',
  supportsCloning: false,
  supportsNativeSpeed: true,
  supportsExaggeration: false,
  canExportFile: true,

  ensureLoaded: async (modelId: string, onProgress?: (info: LoadProgress) => void) => {
    if (loaded[modelId]) {
      onProgress && onProgress({ status: 'Model sẵn sàng.', progress: 1 });
      return;
    }
    onProgress && onProgress({ status: 'Đang tải ONNX Runtime…', progress: 0.05 });
    const ort = await loadOrt();

    const entry = getModelById(modelId);
    const repoKey = resolveRepo(modelId, (entry && entry.repo) || modelId);
    const hf = PIPER_HF[repoKey];
    if (!hf) throw new Error('Không tìm thấy Piper voice: ' + modelId);

    onProgress && onProgress({ status: 'Đang tải cấu hình…', progress: 0.1 });
    const config = await fetchJson(hf.json);
    onProgress && onProgress({ status: 'Đang tải model Piper…', progress: 0.15 });
    const buf = await fetchArrayBuffer(hf.onnx, onProgress);

    onProgress && onProgress({ status: 'Đang khởi tạo session…', progress: 0.96 });
    const session = await ort.InferenceSession.create(buf, {
      executionProviders: ['wasm'],
    });

    const sampleRate =
      (config.audio && config.audio.sample_rate) ||
      (config.audio && config.audio.sampleRate) ||
      22050;

    loaded[modelId] = {
      session,
      config,
      sampleRate,
      voiceId: hf.voiceId,
      label: hf.label,
      lang: hf.lang,
    };
    onProgress && onProgress({ status: 'Model sẵn sàng.', progress: 1 });
  },

  isReady: (modelId: string) => !!loaded[modelId],

  listVoices: async (modelId: string) => {
    const b = loaded[modelId];
    if (!b) return [];
    const v: PresetVoice = {
      id: b.voiceId,
      label: b.label,
      lang: b.lang,
    };
    return [v];
  },

  synthesize: async (modelId: string, opts: SynthOptions): Promise<SynthResult> => {
    abortFlag = false;
    if (!loaded[modelId]) {
      await piperEngine.ensureLoaded(modelId, opts.onProgress);
    }
    const bundle = loaded[modelId];
    const text = (opts.text || '').trim();
    if (!text) throw new Error('Hãy nhập văn bản.');
    const speed = typeof opts.speed === 'number' && opts.speed > 0 ? opts.speed : 1;
    const sentences = splitSentences(text);
    const chunks: Float32Array[] = [];
    for (let i = 0; i < sentences.length; i++) {
      if (abortFlag || (opts.signal && opts.signal.aborted)) {
        throw new Error('Đã hủy.');
      }
      opts.onProgress &&
        opts.onProgress({
          status: 'Đang tổng hợp ' + (i + 1) + '/' + sentences.length + '…',
          progress: (i + 1) / sentences.length,
        });
      const pcm = await runPiper(bundle, sentences[i], speed);
      chunks.push(pcm);
      // small silence between sentences
      chunks.push(new Float32Array(Math.floor(bundle.sampleRate * 0.15)));
    }
    return {
      pcm: concatPcm(chunks),
      sampleRate: bundle.sampleRate,
      nativeSpeedApplied: true,
    };
  },

  cancel: () => {
    abortFlag = true;
  },

  unload: async (modelId: string) => {
    delete loaded[modelId];
  },
};
